'use client'

import AppIcon from "@/app/components/atomic/AppIcon"
import ChoiceCardGroup, { ChoiceCardOption } from "@/app/components/atomic/molecules/ChoiceCardGroup"
import QuestionLabel from "@/app/components/atomic/molecules/QuestionLabel"
import {
    PRETTO_JOURNEY_ANCHOR_ID,
    PRETTO_JOURNEY_OPTIONS,
    PRETTO_UTM_CONTENT,
    PRETTO_UTM_TERM,
} from "@/app/constants/prettoJourney"
import { CookiesEnum } from "@/app/enums/Cookies"
import { getCookie } from "@/app/utils/client-cookies"
import { buildPrettoMaturityUrl } from "@/app/utils/pretto-url"
import { useTracking } from "@/app/contexts/tracking-context"
import Image from "next/image"

const journeyOptions: ChoiceCardOption<string>[] = PRETTO_JOURNEY_OPTIONS.map((option) => ({
    value: option.id,
    title: option.title,
    description: option.text,
    icon: option.icon,
    dataTest: `pretto-option-${option.id.toLowerCase()}`,
}))

export default function PrettoJourneyWidget() {
    const { track } = useTracking()

    const handleSelect = (id: string) => {
        const option = PRETTO_JOURNEY_OPTIONS.find((opt) => opt.id === id)
        if (!option) return

        // Read on click, not useSearchParams: that hook needs a <Suspense> that drops this block from the prerender.
        const gclid = new URLSearchParams(window.location.search).get("gclid")
            || getCookie(CookiesEnum.USER_GCLID)
            || undefined
        const userToken = getCookie(CookiesEnum.USER_TRACKING_TOKEN) || undefined
        const url = buildPrettoMaturityUrl(
            option.id,
            option.kind,
            { content: PRETTO_UTM_CONTENT.JOURNEY, term: PRETTO_UTM_TERM.PRET_IMMOBILIER },
            gclid,
            userToken,
        )

        track({
            event: "gtm_partner_link_clic",
            partner_url: url,
            event_source: "pretto_journey_cta",
        })

        window.location.assign(url)
    }

    return (
        <div
            id={PRETTO_JOURNEY_ANCHOR_ID}
            className="flex flex-col gap-5 rounded-3xl bg-white p-5 shadow-[0_4px_20px_rgba(0,0,0,0.06)] md:p-6"
            data-test="pretto-journey"
        >
            <div className="flex items-center justify-between gap-3">
                <h2 className="block px-0 text-2xl">Démarrez votre projet</h2>
                <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-pastel-yellow px-3 py-1 text-sm font-semibold text-pale">
                    <AppIcon iconClassName="fa-clock" size="sm" />1 min
                </span>
            </div>

            <div className="flex flex-col gap-3">
                <QuestionLabel number={1}>Où en êtes-vous dans votre projet immobilier ?</QuestionLabel>
                <ChoiceCardGroup
                    options={journeyOptions}
                    onChange={handleSelect}
                    indicator="chevron"
                    ariaLabel="Où en êtes-vous dans votre projet immobilier ?"
                />
            </div>

            <div className="flex flex-col xxs:flex-row justify-between gap-2 w-full items-center">
                <span className="flex gap-2 text-[13px] text-rm-gray items-center">
                    <AppIcon iconClassName="fa-lock" size="xs" />
                    Gratuit, sans engagement
                </span>

                <div className="flex items-center justify-center gap-2 text-[13px] text-rm-gray">
                    <span className="hidden xxs:block">
                        avec
                    </span>

                    <Image
                        src="/assets/partners/reassurez-moi-logo-new.png"
                        alt="Réassurez-moi"
                        width={140}
                        height={40}
                        className="h-4 w-auto object-contain"
                    />
                    <span className="text-2xl font-light text-rm-gray">|</span>
                    <Image
                        src="/assets/partners/pretto-logo.svg"
                        alt="Pretto"
                        width={158}
                        height={40}
                        className="h-4 w-auto object-contain"
                    />
                </div>
            </div>
        </div>
    )
}
